import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
  faCalendarAlt,
  faFilter,
  faChevronDown,
  faArrowsAlt,
  faEllipsisV,
  faEdit,
  faSort,
  faSortUp,
  faSortDown,
  faPaperPlane,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import Cookies from "js-cookie";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import { useGetAllTemplateQuery } from "../services/useGetAllTemplateQuery";
import { cpAgreementsEnabled } from "../config/envs";

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [advancedSearchType, setAdvancedSearchType] = useState("name");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isAdvancedSearchDropdownOpen, setIsAdvancedSearchDropdownOpen] =
    useState(false);
  const [openTemplateId, setOpenTemplateId] = useState(null);

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [typeFilter, setTypeFilter] = useState("all");

  const dateOptions = [
    { label: "", value: "" },
    {
      label: "Last 12 months",
      value: moment().subtract(12, "months").format("YYYY-MM-DD"),
    },
    {
      label: "Last 6 months",
      value: moment().subtract(6, "months").format("YYYY-MM-DD"),
    },
    {
      label: "Last 30 days",
      value: moment().subtract(30, "days").format("YYYY-MM-DD"),
    },
    {
      label: "Last week",
      value: moment().subtract(7, "days").format("YYYY-MM-DD"),
    },
  ];

  const advancedSearchOptions = [
    { label: "Template name", value: "name" },
    { label: "Template ID", value: "id" },
    { label: "Email Subject", value: "email_subject" },
  ];

  const TEMPLATE_TYPE_LABELS = {
    provider: "Provider",
    collab_physician: "Collab Physician",
    generic: "Generic",
  };
  const typeFilterOptions = [
    { label: "All Types", value: "all" },
    { label: "Provider", value: "provider" },
    { label: "Collab Physician", value: "collab_physician" },
    { label: "Generic", value: "generic" },
  ];

  const dateDropdownRef = useRef(null);
  const advancedSearchDropdownRef = useRef(null);
  const optionsRef = useRef(null);

  const toggleDateDropdown = () => {
    setIsDateDropdownOpen(!isDateDropdownOpen);
  };

  const toggleAdvancedSearchDropdown = () => {
    setIsAdvancedSearchDropdownOpen(!isAdvancedSearchDropdownOpen);
  };

  const toggleTemplateOptions = (templateId) => {
    setOpenTemplateId(openTemplateId === templateId ? null : templateId);
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit,
      template_type: typeFilter,
    };
    if (debouncedSearchTerm) {
      params[advancedSearchType] = debouncedSearchTerm;
    }

    if (dateFilter) {
      params.startDate = dateFilter.value
        ? moment(dateFilter.value).startOf("day").format("YYYY-MM-DD")
        : null;
      params.endDate = dateFilter.value
        ? moment().endOf("day").format("YYYY-MM-DD")
        : null;
    }
    return params;
  }, [page, limit, debouncedSearchTerm, dateFilter, advancedSearchType, typeFilter]);

  const {
    data: apiData,
    isLoading,
    error,
  } = useGetAllTemplateQuery(queryParams);

  useEffect(() => {
    if (apiData?.data?.templates) {
      setTemplates(apiData.data.templates);
      setTotalPages(apiData.data.pagination.totalPages);
      setTotalRecords(apiData.data.pagination.total);
    }
  }, [apiData]);

  const filteredTemplates = useMemo(() => {
    return templates;
  }, [templates]);

  const sortTemplates = useCallback((templatesToSort, column, direction) => {
    if (!column || !templatesToSort) {
      return templatesToSort;
    }

    const sorted = [...templatesToSort].sort((a, b) => {
      let valueA = a[column];
      let valueB = b[column];

      if (valueA === null || valueA === undefined) valueA = "";
      if (valueB === null || valueB === undefined) valueB = "";

      if (sortColumn === "created_at" || sortColumn === "updated_at") {
        valueA = new Date(valueA).getTime();
        valueB = new Date(valueB).getTime();
      } else if (typeof valueA === "string") {
        valueA = valueA.toLowerCase();
        valueB = valueB.toLowerCase();
      }

      if (valueA < valueB) {
        return direction === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return direction === "asc" ? 1 : -1;
      }

      return 0;
    });

    return sorted;
  }, []);

  const sortedTemplates = useMemo(() => {
    return sortTemplates(filteredTemplates, sortColumn, sortDirection);
  }, [filteredTemplates, sortColumn, sortDirection, sortTemplates]);

  const handleSort = useCallback(
    (column) => {
      setSortColumn(column);
      setSortDirection((prevDirection) =>
        column === sortColumn
          ? prevDirection === "asc"
            ? "desc"
            : "asc"
          : "asc"
      );
    },
    [sortColumn]
  );

  const getSortIcon = (column) => {
    if (column === sortColumn) {
      return sortDirection === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDateFilter("");
    setAdvancedSearchType("name");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleDateFilterChange = (option) => {
    setDateFilter(option);
    setIsDateDropdownOpen(false);
    setPage(1);
  };

  const handleAdvancedSearchChange = (option) => {
    setAdvancedSearchType(option);
    setIsAdvancedSearchDropdownOpen(false);
    setPage(1);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(event.target)
      ) {
        setIsDateDropdownOpen(false);
      }

      if (
        advancedSearchDropdownRef.current &&
        !advancedSearchDropdownRef.current.contains(event.target)
      ) {
        setIsAdvancedSearchDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dateDropdownRef, advancedSearchDropdownRef]);

  const handleEditTemplate = (template) => {
    navigate(`/copy-template`, {
      state: { templateData: template },
    });
  };

  const handleUseTemplate = (template) => {
    navigate("/send", { state: { data: template } });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value, 10);
    setLimit(newItemsPerPage);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-screen">
        <ClipLoader color="#007bff" size={40} />
        <span className="ml-2">Loading templates...</span>
      </div>
    );
  }

  if (error) {
    let errorMessage = "An unexpected error occurred.";
    try {
      if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage = parsedError.message || errorMessage;
        } catch (jsonParseError) {
          errorMessage = error.message;
        }

        if (errorMessage === "Invalid or expired token") {
          Cookies.remove("token");
          Cookies.remove("user");
          navigate("/login", { replace: true });
        }
      } else {
        errorMessage = "An unknown error occurred.";
      }
    } catch (e) {
      console.error("Error during error handling:", e);
      errorMessage = "An error occurred while processing the error.";
    }

    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4">My Templates</h1>
        <div className="text-center py-8">
          <h2 className="text-2xl font-semibold text-red-500">
            Error: Could not load templates.
          </h2>
          <p className="text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">My Templates</h1>
        <div className="flex flex-wrap gap-3 items-center">
        {cpAgreementsEnabled && <Link to="/cp-document-drafts" className="text-purple-700 underline text-sm">CP document templates</Link>}
        <Link to="/prepare-template">
          <button className=" bg-[#cfc9ff] text-black hover:bg-[#948ae3] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
            Create Template
          </button>
        </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center space-x-4 mb-4">
        <div className="relative w-full sm:w-1/3 mb-2 sm:mb-0">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Search My Templates"
            className="w-full py-2 px-8 border rounded focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] pl-10"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        <div className="relative mb-2 sm:mb-0" ref={dateDropdownRef}>
          <button
            onClick={toggleDateDropdown}
            className="flex items-center py-2 px-4 border rounded focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
          >
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
            {dateFilter.label || "All time"}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`ml-2 ${isDateDropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isDateDropdownOpen && (
            <div className="absolute left-0 mt-2 w-48 bg-white border rounded shadow-md z-10">
              {dateOptions.map((option) => (
                <label
                  key={option.label}
                  className="block w-full text-left py-2 px-4 hover:bg-gray-100 focus:outline-none flex items-center"
                >
                  <input
                    type="radio"
                    className="form-radio h-5 w-5 text-[#cfc9ff] mr-2"
                    value={option.value}
                    checked={dateFilter === option.value}
                    onChange={() => handleDateFilterChange(option)}
                  />
                  <span>{option.label || "All time"}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="relative mb-2 sm:mb-0" ref={advancedSearchDropdownRef}>
          <button
            onClick={toggleAdvancedSearchDropdown}
            className="flex items-center py-2 px-4 border rounded focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            {
              advancedSearchOptions.find(
                (option) => option.value === advancedSearchType
              )?.label
            }{" "}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`ml-2 ${
                isAdvancedSearchDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {isAdvancedSearchDropdownOpen && (
            <div className="absolute left-0 mt-2 w-48 bg-white border rounded shadow-md z-10">
              {advancedSearchOptions.map((option) => (
                <label
                  key={option.value}
                  className="block w-full text-left py-2 px-4 hover:bg-gray-100 focus:outline-none flex items-center"
                >
                  <input
                    type="radio"
                    className="form-radio h-5 w-5 text-[#cfc9ff] mr-2"
                    value={option.value}
                    checked={advancedSearchType === option.value}
                    onChange={() => handleAdvancedSearchChange(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="relative mb-2 sm:mb-0">
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="py-2 px-4 border rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#cfc9ff]"
            aria-label="Filter by template type"
          >
            {typeFilterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleClearSearch}
          className="text-gray-600 hover:text-gray-800 focus:outline-none"
        >
          Clear Filters
        </button>
      </div>

      <div className="overflow-x-auto  sm:rounded-lg">
        {sortedTemplates.length === 0 ? (
          <div className="text-center py-8 h-screen">
            <h2 className="text-2xl font-semibold text-gray-700">No results</h2>
            <p className="text-gray-500">
              Try editing your search term, filters, or date range.
            </p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("id")}
                  >
                    ID
                    <FontAwesomeIcon
                      icon={getSortIcon("id")}
                      className="ml-1"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("name")}
                  >
                    Name
                    <FontAwesomeIcon
                      icon={getSortIcon("name")}
                      className="ml-1"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("template_type")}
                  >
                    Type
                    <FontAwesomeIcon
                      icon={getSortIcon("template_type")}
                      className="ml-1"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("email_subject")}
                  >
                    Email Subject
                    <FontAwesomeIcon
                      icon={getSortIcon("email_subject")}
                      className="ml-1"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("created_at")}
                  >
                    Created Date
                    <FontAwesomeIcon
                      icon={getSortIcon("created_at")}
                      className="ml-1"
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 cursor-pointer"
                    onClick={() => handleSort("updated_at")}
                  >
                    Last Change
                    <FontAwesomeIcon
                      icon={getSortIcon("updated_at")}
                      className="ml-1"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((template) => (
                  <tr
                    key={template.id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {template.id}
                    </th>
                    <td className="px-6 py-4">{template.name}</td>
                    <td className="px-6 py-4">
                      {TEMPLATE_TYPE_LABELS[template.template_type] ||
                        template.template_type ||
                        "-"}
                    </td>
                    <td className="px-6 py-4">{template.email_subject}</td>
                    <td className="px-6 py-4">
                      {moment(template.created_at).format("MM/DD/YYYY hh:mm A")}
                    </td>
                    <td className="px-6 py-4">
                      {moment(template.updated_at).format("MM/DD/YYYY hh:mm A")}
                    </td>
                    <td className="px-6 py-4 relative">
                      <div className="flex items-center space-x-2 ">
                        <button
                          onClick={() => handleUseTemplate(template)}
                          className=" bg-[#cfc9ff] text-black hover:bg-[#948ae3] font-medium py-2 px-4 rounded-md shadow-md focus:outline-none focus:ring-2 focus:ring-[#cfc9ff] focus:ring-opacity-50 transition-colors duration-200 inline-flex items-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon
                            icon={faPaperPlane}
                            className="mr-1"
                          />
                          Use
                        </button>
                        <button
                          onClick={() => navigate(`/template/${template.id}`)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition-colors duration-200 inline-flex items-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faEdit} className="mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition-colors duration-200 inline-flex items-center gap-2 text-sm"
                        >
                          <FontAwesomeIcon icon={faCopy} className="mr-1" />
                          Copy
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center mt-4">
              <div className="flex items-center">
                <label htmlFor="itemsPerPage" className="mr-2 text-sm">
                  Items per page
                </label>

                <div className="relative">
                  <select
                    id="itemsPerPage"
                    className="appearance-none border rounded px-4 py-2 text-sm pr-8"
                    value={limit}
                    onChange={handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 rounded"
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNumber =
                    Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`px-4 py-2 mx-1 rounded ${
                        page === pageNumber
                          ? "bg-[#cfc9ff] text-black hover:bg-[#948ae3]"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 rounded"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TemplatesPage;
